import React, { ReactElement, RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Person } from '@enview/interface/types/Person';
import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import {
  AnnotationBillDetails,
  UserAction,
} from '@enview/interface/types/actions/UserAction';
import './ActivityCard.scss';
import 'react-tooltip/dist/react-tooltip.css';
import {
  ArchiveButton,
  DeleteButton,
  DownloadFileButton,
  ReplyButton,
  StarButton,
} from './ActivityCardButtons';
import UserIcon from '../../Icons/UserIcon';
import FileIcon from '../../Icons/FileIcon';
import TextButton from '../../Buttons/TextButton';
import {
  getCardRef,
  getLineRef,
  getTeamMode,
  openEditUserActionModal,
  openReplyUserActionModal,
  setBillSidebarCurrentTab,
  setSelectedCardId,
} from '../../../../dux';
import { mobileMediaQuery } from '../../../../helpers/mediaQueries';
import * as Analytics from '../../../../analytics/UserActionAnalytics';
import { State } from '../../../../dux/@types';
import {
  convertIdToAbbreviatedTitle,
  getAbbreviatedBillTitle,
  getBillAlias,
  BillSidebarTabs,
} from '../../../../helpers/BillHelper';
import { ActivityCard, ActivityCardStyle } from './ActivityCard';
import { serializeToReactElement } from '@enview/interface/utils/RichTextAnnotation';

type UserActivityCardProps = {
  action: UserAction;
  orgUser: OrganizationUser;
  selectedBillVersion?: number;
  setSelectedBillVersion?: (version: number) => void;
};

enum Descriptions {
  annotation = 'left an annotation',
  comment = 'left a note',
  fileUpload = 'attached a file',
  reply = 'replied to',
}

const getUserActivityTitle = (
  name: string,
  description: Descriptions,
  onBillString: ReactElement | undefined,
  fileName?: string,
  replyToAction?: UserAction,
): ReactElement => {
  const replyToUser = replyToAction?.organizationUser;
  return (
    <span className="">
      <b>{name}</b>
      &nbsp;{description}
      {fileName && <span style={{ fontStyle: 'italic' }}>&nbsp;({fileName})</span>}
      {replyToUser && (
        <span>
          &nbsp;
          {replyToUser.firstName} {replyToUser.lastName}
        </span>
      )}
      {onBillString}
    </span>
  );
};

const teamViewBillDescription = (
  onBillLinkClicked: (actionId?: number) => void,
  teamModeId: number,
  actionId: number,
  billId: string,
  billInfo?: AnnotationBillDetails,
): ReactElement | undefined => {
  const title = convertIdToAbbreviatedTitle(billId);
  if (!billInfo) {
    return (
      <>
        <span>&nbsp;on&nbsp;</span>
        <Link
          className="activity-card-link"
          onClick={() => {
            onBillLinkClicked(actionId);
          }}
          to={`/legislative-tracking/bill/details/${billId}`}
        >
          {title}
        </Link>
      </>
    );
  }
  const { name, number, session, userAliases } = billInfo;
  const billAlias = getBillAlias(userAliases, teamModeId);
  const tooltip = `${title} - ${name}${billAlias ? ` (${billAlias.alias})` : ''}`;
  const billString = getAbbreviatedBillTitle(session, number, billAlias);
  return (
    <>
      <span>&nbsp;on&nbsp;</span>
      <Link
        className="activity-card-link"
        data-tooltip-content={tooltip}
        id={`${actionId}-team`}
        onClick={() => {
          onBillLinkClicked(actionId);
        }}
        to={`/legislative-tracking/bill/details/${billId}`}
      >
        {`${billString}`}
      </Link>
      <ReactTooltip
        anchorId={`${actionId}-team`}
        className="card-tooltip tooltip-content"
        id={`${actionId}-team`}
        place="right"
      />
    </>
  );
};

const teamViewPersonDescription = (actionId: number, person: Person): ReactElement => {
  return (
    <>
      <span>&nbsp;on</span>
      <Link className="activity-card-link" to={`/person/${person.id}/activity`}>
        {` ${person.name}`}
      </Link>
    </>
  );
};

type SnippetProps = {
  action: UserAction;
  isBillViewFeed: boolean;
  selectedBillVersion?: number;
  setSelectedBillVersion?: (version: number) => void;
};

const Snippet = (props: SnippetProps): ReactElement => {
  const { action, isBillViewFeed, selectedBillVersion, setSelectedBillVersion } = props;
  const { annotation } = action;
  const refObject = useSelector((state: State) => {
    return getLineRef(state, `${annotation && annotation.fromLine}`);
  });
  // Check if bill is set and being viewed in BillDisplay
  const isMobile = mobileMediaQuery.matches;

  const onClick = (ref: RefObject<HTMLDivElement>): void => {
    Analytics.trackJumpToText(action);
    if (
      action.billData?.billVersionId &&
      action.billData.billVersionId != selectedBillVersion &&
      setSelectedBillVersion
    ) {
      setSelectedBillVersion(action.billData?.billVersionId);
    } else {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <div className="d-flex flex-column snippet">
      <span>{annotation && annotation.selectedText}</span>
      {!isMobile && isBillViewFeed && refObject && (
        <TextButton
          className="jump-to-text"
          label="Jump to text"
          onClick={() => onClick(refObject)}
        />
      )}
    </div>
  );
};

const UserActivityCard = (props: UserActivityCardProps): ReactElement => {
  const { action, orgUser, selectedBillVersion, setSelectedBillVersion } = props;

  const buttons: ReactElement[] = [];
  const name = `${action.organizationUser.firstName} ${action.organizationUser.lastName}`;
  const authoredAnnotation = action.organizationUser.id === orgUser.id;
  const file =
    action.userFiles && action.userFiles.length > 0 ? action.userFiles[0] : undefined;
  const dispatch = useDispatch();
  const ref = useSelector((state: State) => getCardRef(state, action.id));
  const teamModeId = useSelector(getTeamMode);
  const isTeamView = window.location.pathname.includes('teams');
  const selectedCardId = useSelector((state: State) => state.billView.selectedCardId);
  const onBillLinkClicked = (actionId?: number): void => {
    dispatch(setBillSidebarCurrentTab(BillSidebarTabs.ACTIVITY));
    if (actionId) {
      dispatch(setSelectedCardId(actionId));
    }
  };
  useEffect(() => {
    if (action.id === selectedCardId && ref && ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      dispatch(setSelectedCardId(undefined));
    }
  }, [dispatch, selectedCardId, action, ref]);

  let teamDescription: ReactElement | undefined;

  if (isTeamView && action.billData && action.billData.bill) {
    teamDescription = teamViewBillDescription(
      onBillLinkClicked,
      teamModeId,
      action.id,
      action.billData.billId,
      action.billData.bill,
    );
  } else if (isTeamView && action.person)
    teamDescription = teamViewPersonDescription(action.id, action.person);

  buttons.push(<StarButton action={action} key={0} />);
  let title;
  if (file) {
    buttons.push(<DownloadFileButton fileId={file.id} key={1} />);
    title = getUserActivityTitle(
      name,
      Descriptions.fileUpload,
      teamDescription,
      file.filename,
    );
  } else if (action.replyTo && action.replyTo.organizationUser) {
    title = getUserActivityTitle(
      name,
      Descriptions.reply,
      teamDescription,
      '',
      action.replyTo,
    );
  } else if (action.annotation) {
    title = getUserActivityTitle(name, Descriptions.annotation, teamDescription);
  } else {
    title = getUserActivityTitle(name, Descriptions.comment, teamDescription);
  }

  buttons.push(
    <ReplyButton
      key={2}
      openReplyEditor={() => dispatch(openReplyUserActionModal(action))}
    />,
  );
  if (authoredAnnotation) {
    buttons.push(<ArchiveButton action={action} key={3} />);
    buttons.push(<DeleteButton action={action} key={4} />);
  }

  const timeUpdate = new Date(action.updatedAt).toLocaleDateString();

  /*   const serializePlain = (nodes: RichTextNode[]) => {
    if (nodes) return nodes.map((n) => Node.string(n)).join('\n');
    return '';
  }; */
  const { firstName, lastName } = action.organizationUser;

  return (
    <ActivityCard
      activityStyle={ActivityCardStyle.USER_ACTIVITY}
      annotation={
        action.annotation && (
          <Snippet
            action={action}
            isBillViewFeed={!isTeamView}
            selectedBillVersion={selectedBillVersion}
            setSelectedBillVersion={setSelectedBillVersion}
          />
        )
      }
      buttons={buttons}
      content={
        action.commentJson ? (
          <span>{serializeToReactElement(action.commentJson, orgUser.id)}</span>
        ) : (
          <span>{action.comment}</span>
        )
      }
      edit={() => dispatch(openEditUserActionModal(action))}
      icon={
        file ? <FileIcon /> : <UserIcon firstName={firstName} lastName={lastName} />
      }
      iconColor="seagreen"
      isEditable={authoredAnnotation}
      refObject={ref}
      subtitle={timeUpdate}
      title={title}
    />
  );
};

export default UserActivityCard;
